import { GOOGLE_MAPS_API_BASE } from "@/config/googleMaps";
import { languageUtils } from "@/utils/languageUtils";
import type { DailyCommuteLocation } from "@/types/dailyCommuteLocation";

export interface RouteEstimate {
  distanceText: string;
  distanceKm: number;
  durationText: string;
  durationMinutes: number;
}

interface DistanceMatrixResponse {
  rows?: Array<{
    elements?: Array<{
      status: string;
      distance?: { text: string; value: number };
      duration?: { text: string; value: number };
    }>;
  }>;
  status: string;
  error_message?: string;
}

function toRoutePoint(place: DailyCommuteLocation): string {
  if (place.lat && place.lng) {
    return `${place.lat},${place.lng}`;
  }
  return place.address;
}

export async function fetchRouteEstimate(
  origin: DailyCommuteLocation,
  destination: DailyCommuteLocation
): Promise<RouteEstimate> {
  const language = languageUtils.getCurrentLanguage();
  const params = new URLSearchParams({
    origins: toRoutePoint(origin),
    destinations: toRoutePoint(destination),
    mode: "driving",
    units: "metric",
    language,
  });

  const url = `${GOOGLE_MAPS_API_BASE}/distancematrix/json?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Route estimate request failed: ${res.status}`);
  }

  const json = (await res.json()) as DistanceMatrixResponse;
  const element = json.rows?.[0]?.elements?.[0];

  if (json.status !== "OK" || !element || element.status !== "OK") {
    throw new Error(json.error_message ?? "Unable to calculate route distance");
  }

  if (!element.distance || !element.duration) {
    throw new Error("Route estimate returned incomplete data");
  }

  return {
    distanceText: element.distance.text,
    distanceKm: element.distance.value / 1000,
    durationText: element.duration.text,
    durationMinutes: Math.round(element.duration.value / 60),
  };
}
